import { Facility } from '@reliance/types/models/Facility';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { useFacilityGames, useGames } from '../hooks/Game';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';

type FacilityGamesTabProps = {
  facility: Facility;
};

const FacilityGamesTab: React.FC<FacilityGamesTabProps> = ({ facility }) => {
  const filter = useMemo(() => ({ facilityId: facility.id }), [facility.id]);
  const assignedGames = useFacilityGames(filter);
  const allGames = useGames();
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastAssignedGames, setLastAssignedGames] = useState(assignedGames.result?.data);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  useNotifyTabLoading('games', assignedGames.loading || allGames.loading || isUpdating);

  // Maintain a list of the previous assignments to avoid the UI flashing when toggling games
  useEffect(() => {
    if (!assignedGames.loading && assignedGames.result?.data) {
      setLastAssignedGames(assignedGames.result.data);
    }
  }, [assignedGames.result?.data, assignedGames.loading]);

  // Handle a single game toggle
  const handleGameToggle = async (gameId: number, isChecked: boolean) => {
    setIsUpdating(true);
    try {
      const currentGameIds = assignedGames.result?.data?.map((ag) => ag.game.id) || [];
      const newGameIds = isChecked ? [...currentGameIds, gameId] : currentGameIds.filter((id) => id !== gameId);

      await api.put('/game/assign-full', {
        facilityId: facility.id,
        gameIds: newGameIds,
      });
      await assignedGames.execute();
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle a toggle of all games
  const handleToggleAll = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      const gameIds = checked ? allGames.result?.map((g) => g.id) : [];
      await api.put('/game/assign-full', {
        facilityId: facility.id,
        gameIds: gameIds || [],
      });
      await assignedGames.execute();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <ConfirmModal
        show={showDisableConfirm}
        title="Disable All Games"
        rejectText="Disable"
        onAccept={() => handleToggleAll(false)}
        onClose={() => setShowDisableConfirm(false)}>
        Are you sure you want to disable all games for this facility?
      </ConfirmModal>

      <Card>
        <Card.Body>
          <Row className="mb-4">
            <Col sm={12}>
              <Button
                variant="secondary"
                style={{ marginRight: 10 }}
                disabled={isUpdating}
                onClick={() => handleToggleAll(true)}>
                Enable All
              </Button>
              <Button variant="secondary" disabled={isUpdating} onClick={() => setShowDisableConfirm(true)}>
                Disable All
              </Button>
            </Col>
          </Row>

          <Row>
            {allGames.result
              ?.sort((a, b) => a.title.localeCompare(b.title))
              ?.map((game) => {
                const isAssigned = lastAssignedGames?.some((assigned) => assigned.game.id === game.id);

                return (
                  <Col sm={6} md={4} key={game.id} className="mb-3">
                    <Form.Check
                      className="game-checkbox"
                      type="checkbox"
                      id={`game-${game.id}`}
                      checked={isAssigned}
                      disabled={isUpdating}
                      onChange={(e) => handleGameToggle(game.id, e.target.checked)}
                      label={game.title}
                    />
                  </Col>
                );
              })}
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export const facilityGamesTabMeta = {
  heading: 'Games',
  style: 'fas fa-fw fa-lg fa-gamepad',
  permissions: ['game_manage'],
};

export default FacilityGamesTab;
